// @flow
import { useMemo } from 'react';
import moment from 'moment-timezone';
import isEmpty from 'ramda/es/isEmpty';
import flatten from 'ramda/es/flatten';
import map from 'ramda/es/map';
import reduce from 'ramda/es/reduce';
import filter from 'ramda/es/filter';
import pipe from 'ramda/es/pipe';

import type { Klass } from 'app/dataTypes/Klasses.types';
import SimpleDate from 'app/dataProviders/SimpleDate';


type Options = {
  date?: string,
  dateFormat: string,
  klass: Klass | null,
  alignByWeek?: boolean,
}

export default function useDateValid({
  date,
  dateFormat,
  klass,
  alignByWeek,
}: Options): null | SimpleDate {
  const dateValid = useMemo(() => {
    if (!klass) {
      return null;
    }
    const klassPeriods = klass.schoolyearPeriods;
    const periods = (
      alignByWeek && klassPeriods
        ? klassPeriods.map(({ start, finish }) => ({
          start: new SimpleDate(...start.startOfWeek()),
          finish: new SimpleDate(...finish.endOfWeek()),
        }))
        : klassPeriods
    );

    if (periods && !isEmpty(periods)) {
      if (date) {
        const simpleDate = new SimpleDate(date, dateFormat);

        const isFitToPeriods = periods.find(({ start, finish }) => simpleDate.isBetween(start, finish));
        if (isFitToPeriods) {
          return simpleDate;
        }
      }
      const today = new SimpleDate(moment().format('YYYY-MM-DD'), 'YYYY-MM-DD');
      const isFitToPeriods = periods.find(({ start, finish }) => today.isBetween(start, finish));
      if (isFitToPeriods) {
        return today;
      }

      const dates: Array<SimpleDate> = pipe(
        map(({ start, finish }) => ([start, finish])),
        flatten,
        filter((item: SimpleDate) => item.getIsValid())
      )(periods);

      if (!isEmpty(dates)) {
        const nearestStart = [dates[0], Math.abs(today.diff(dates[0]))];
        const [nearestSimpleDate]: [SimpleDate, number] = reduce(([prev, prevDiff], someDate) => {
          const currDiff = Math.abs(today.diff(someDate));
          return currDiff > prevDiff ? [prev, prevDiff] : [someDate, currDiff];
        }, nearestStart, dates);

        return nearestSimpleDate;
      }
    }

    return null;
  }, [date, dateFormat, klass, alignByWeek]);

  return dateValid;
}
